import { useQuery, useMutation } from "react-query";
import { http } from "src/api/http";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import { InputAdornment, IconButton, TextField, Box, Switch, Button, Tooltip, Paper, Alert, Menu, MenuItem } from "@mui/material";
import { FansUrl } from "./models";
import { useRef, useState, useEffect } from "react";
import captureFormImage from "@arelia-assets/captureFormImage.png";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useBrandRegistrationStatusAPI } from "src/features/fans/settings/settings.api";
import { toast } from "sonner";

export default function FansUrlContainer() {
  const inputRef = useRef<HTMLInputElement>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [copied, setCopied] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const { data: registrationStatus, isLoading: regLoading } = useBrandRegistrationStatusAPI(true, false);
  const isRegistrationComplete = registrationStatus?.completed ?? false;

  const { data, isLoading, isError } = useQuery<FansUrl>({
    queryKey: ['/api/my-fans/form-url'],
    queryFn: () => http('/api/my-fans/form-url'),
  });

  const { data: formEnabledState } = useQuery<boolean>({
    queryKey: ['/api/my-fans/capture-form-status'],
    queryFn: () => http('/api/my-fans/capture-form-status'),
  });

  const { mutate: uploadCaptureFormBackground, isLoading: isUploading } = useMutation(
    (file: File) => {
      const formData = new FormData();
      formData.append('ImageFile', file);

      return fetch(`/api/account/capture-form-background`, {
        method: 'POST',
        body: formData,
      });
    },
    {
      onSuccess: () => {
        toast.success("Cover image changed successfully");
      }
    }
  );

  useEffect(() => {
    if (regLoading === false && formEnabledState !== undefined) {
      if (isRegistrationComplete) {
        setIsEnabled(formEnabledState);
      } else {
        setIsEnabled(true);
      }
    }
  }, [regLoading, formEnabledState, isRegistrationComplete]);

  const { mutate: updateFormEnabled } = useMutation(
    (enabled: boolean) => http('/api/my-fans/capture-form-control', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(enabled)
    })
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleChangeBanner = () => {
    handleMenuClose();
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadCaptureFormBackground(file);
    }
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleInputClick = () => {
    inputRef.current?.select();
  }

  const handleViewClick = () => {
    const url = `${globalThis.location.origin}${data?.url}`;
    window.open(url, '_blank');
  }

  const handleCopyClick = () => {
    const url = `${globalThis.location.origin}${data?.url}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const toggleEnabled = () => {
    if (isRegistrationComplete) {
      const newState = !isEnabled;
      setIsEnabled(newState);
      updateFormEnabled(newState);
    }
  }

  const shareableUrl = data ? `${globalThis.location.origin}${data.url}` : '';

  return (
    <WidgetCard
      widgetId={"FANS_URL"}
      title="Share this sign up form with your fans"
      extraActions={
        <Box sx={styles.formControl}>
          <Tooltip
            title={!isRegistrationComplete ? "Complete Twilio registration to toggle form status" : ""}
            placement="top"
          >
            <span>
              <Switch
                checked={isEnabled}
                onChange={toggleEnabled}
                color="primary"
                disabled={isLoading || isError || !isRegistrationComplete}
              />
            </span>
          </Tooltip>
          <IconButton
            aria-label="more"
            onClick={handleMenuClick}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleChangeBanner} disabled={isUploading}>
              Change Cover image
            </MenuItem>
          </Menu>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*"
            onChange={handleFileChange}
          />
        </Box>
      }
      loading={isLoading}
      error={isError}
      disableFavorite={true}
    >
      <Box sx={styles.container}>
        <Box sx={styles.imageContainer}>
          <img
            src={captureFormImage}
            alt="Capture Form"
            style={styles.image}
          />
        </Box>

        <Box sx={styles.contentSection}>
          {isEnabled ? (
            <Box sx={styles.textBoxSection}>
              <Paper variant="outlined" sx={styles.urlContainer}>
                <TextField
                  inputRef={inputRef}
                  value={shareableUrl}
                  variant="standard"
                  fullWidth
                  InputProps={{
                    readOnly: true,
                    disableUnderline: true,
                    sx: styles.urlInput,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={copied ? "Copied!" : "Copy URL to share for collecting fans contact data"} placement="top">
                          <IconButton
                            onClick={handleCopyClick}
                            edge="end"
                            size="small"
                            color={copied ? "success" : "primary"}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  onClick={handleInputClick}
                />
              </Paper>
              <Button
                variant="contained"
                color="primary"
                onClick={handleViewClick}
              >
                View Form
              </Button>
            </Box>
          ) : (
            <Alert severity="info" variant="filled" sx={styles.infoAlert}>
              Enable the capture form using the toggle switch above
            </Alert>
          )}
        </Box>
      </Box>
    </WidgetCard>
  );
}
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  imageContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    padding: '8px',
  },
  contentSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 0',
    flex: 0,
  },
  image: {
    height: '100%',
    width: '60%',
  },
  textBoxSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    width: '100%',
  },
  urlContainer: {
    padding: '8px 12px',
    backgroundColor: '#f5f5f5',
    flex: 1
  },
  urlInput: {
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  formControl: {
    display: 'flex',
    alignItems: 'center'
  },
  infoAlert: {
    py: 0.01
  }
};
